import Chart from "react-apexcharts";
import { OverallParticipation } from "../../../services/api-services";
import { API_RESPONSE } from "../../../utills/app-constant";
import { toastEmitter } from "../../../utills/utilities";
import generateChartImage from "./generateChartImage";
import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const DonutChart = ({ selectedCompanyId, graphName }) => {
  const chartRef = useRef(null); // Reference to the chart
  const [rowData, setRowData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [seriesData, setSeriesData] = useState([0, 0, 0]);

  const chartOptions = useMemo(() => ({
    chart: {
      id: "overall_participation",
      type: "donut",
      animations: { enabled: false },
    },
    labels: ["Completed", "In Progress", "Not Started"],
    colors: ["#28a745", "#ffc107", "#dc3545"],
    legend: { position: "top" },
    markers: { shape: "square" },
    plotOptions: {
      pie: {
        donut: {
          size: "60%",
          labels: {
            show: true,
            total: {
              show: true,
              showAlways: true,
              label: "Total",
              formatter: () => rowData?.totalEmployee || 0,
            },
          },
        },
      },
    },
    stroke: { width: 0 },
    dataLabels: {
      enabled: true,
      formatter: (val, opts) => `${opts.w.config.series[opts.seriesIndex]}`,
      style: { fontSize: "10px" },
    },
    tooltip: {
      enabled: true,
      y: { formatter: (val) => `${val} participants` },
    },
  }), [rowData]);

  const getOverallParticipationData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await OverallParticipation(selectedCompanyId, graphName);
      if (response?.data?.responseCode === 200) {
        setRowData(response?.data);
        setSeriesData([
          response?.data?.attemptedEmployee || 0,
          response?.data?.inProgressEmployee || 0,
          response?.data?.notAttemptedEmployee || 0,
        ]);
      } else if (response?.data?.responseCode === 404) {
        setRowData([]);
      } else {
        toastEmitter("error", response?.data?.message);
      }
    } catch (err) {
      toastEmitter("error", API_RESPONSE.MESSAGE_503);
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId, graphName]);

  useEffect(() => {
    getOverallParticipationData();
  }, [getOverallParticipationData]);

  const totalEmployees = rowData?.totalEmployee || 0;
  const completedEmployees = rowData?.attemptedEmployee || 0;
  const completionPercentage = rowData?.completionPercentage || 0;

  useEffect(() => {
    if (seriesData.some((val) => val > 0)) {
      setTimeout(() => {
        const keypoint = `
        Key Points
        Total Employees = ${totalEmployees} 
        Completion Rate = ${completionPercentage}% (${completedEmployees} Participants)`;
        generateChartImage("overall_participation", "overall_participation", keypoint);
      }, 100);
    }
  }, [seriesData, totalEmployees, completionPercentage, completedEmployees]);


  const exportSummaryToExcel = (summary, fileName = "Overall Participation") => {
    const summaryRows = [
      [`Total Employees = ${summary?.totalEmployee ? summary?.totalEmployee : 0}`],
      [`Completed = ${summary?.attemptedEmployee ? summary?.attemptedEmployee : 0}`],
      [`In Progress = ${summary?.inProgressEmployee ? summary?.inProgressEmployee : 0}`],
      [`Not Started = ${summary?.notAttemptedEmployee ? summary?.notAttemptedEmployee : 0}`],

    ];

    const worksheet = XLSX.utils.aoa_to_sheet(summaryRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Summary");

    //  Use correct MIME type for Excel
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, fileName);
  };

  return (
    <>
      <div className="text-end">
        <button className="px-3 py-2 text-white bg-primary rounded border-0" onClick={() => exportSummaryToExcel(rowData)}>Download Excel</button>
      </div>

      <div className="d-flex chart1" id="basic-bar" data-heading="Overall Participation">

        {loading ? (
          <div className="loader-container">
            <div className="loader"></div>
          </div>
        ) : (
          <Chart options={chartOptions} series={seriesData} type="donut" height={300} ref={chartRef} />
        )}

        <div className="d-flex align-items-center">
          <ul>
            <h4>Key Points</h4>
            <p>Total Employees = {totalEmployees}</p>
            <p>Completion Rate = {completionPercentage}% ({completedEmployees} Participants)</p>
          </ul>
        </div>
      </div>
    </>

  );
};

export default DonutChart;
